import React from 'react';
import { motion } from 'framer-motion';
import { Star, Target, TrendingUp, CheckCircle2, AlertCircle, BookOpen, BarChart2, Clock, Zap, Sparkles, Play, ChevronLeft, Info, Lightbulb, Activity, Award, Brain } from 'lucide-react';
import type { AssessmentReport } from '../types';
import type { ModuleId } from '../../content';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { MIN_ANSWERS_FOR_CLAIM } from '../../mastery';
const MAX_TIER = 16;
/**
 * Ondergrens voordat een uitspraak over een onderwerp mag worden gedaan.
 *
 * Stond hier als los getal `3`. Het leerlab heeft dezelfde grens nodig, en twee
 * losse kopieën betekent dat hetzelfde kind in twee schermen een ander antwoord
 * krijgt op dezelfde vraag zodra er één wordt bijgesteld. Nu één bron.
 */
const MIN_QUESTIONS_FOR_ESTIMATE = MIN_ANSWERS_FOR_CLAIM;
interface AssessmentReportPageProps {
  report: AssessmentReport;
  onExit: () => void;
  onRetake?: () => void;
  onPracticeWeakAreas?: () => void;
  onStartPracticeForModule?: (moduleId: ModuleId) => void;
}
const stagger = (i: number) => ({
  delay: 0.06 * i
});
const fadeUp = {
  initial: {
    opacity: 0,
    y: 16
  },
  animate: {
    opacity: 1,
    y: 0
  },
  transition: {
    duration: 0.35,
    ease: 'easeOut'
  }
};
export function AssessmentReportPage({
  report,
  onExit,
  onRetake,
  onPracticeWeakAreas,
  onStartPracticeForModule
}: AssessmentReportPageProps) {
  const {
    score,
    time,
    byTier,
    estimatedLevels,
    overallEstimatedLevelCode,
    overallExpectedLevelCode,
    overallEstimatedTier,
    overallExpectedTier,
    overallCitoLevel,
    growthIndicators,
    actionPlan,
    strengthsBySkill,
    focusAreasBySkill,
    strongAreas,
    weakAreas,
    suggestedPractice,
    recommendation,
    dokDistribution
  } = report;
  const formatDate = (ts: number) => new Date(ts).toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };
  const hasAnalysis = strengthsBySkill.length > 0 || focusAreasBySkill.length > 0 || strongAreas.length > 0 || weakAreas.length > 0;
  const hasActionPlan = actionPlan.beginMet || actionPlan.gaVerderMet.length > 0 || actionPlan.uitdaging;
  const hasTierData = byTier.length > 0;
  // R3-DOK1 surfacing: only show the DOK card if at least one classified
  // item was answered (i.e. not everything ended up in the `unknown` bucket).
  const dokLevels = dokDistribution ? ([{
    level: 1,
    // De "DOK n"-codes zijn weg uit de labels: dit rapport staat op het
    // scherm van het kind zelf, en een code die alleen een vakgenoot
    // kent voegt daar niets toe. Het werkwoord zegt precies genoeg.
    label: 'Onthouden',
    count: dokDistribution.counts.dok1,
    correct: dokDistribution.correctCounts.dok1,
    accuracy: dokDistribution.accuracy.dok1,
    tone: 'bg-slate-400'
  }, {
    level: 2,
    label: 'Toepassen',
    count: dokDistribution.counts.dok2,
    correct: dokDistribution.correctCounts.dok2,
    accuracy: dokDistribution.accuracy.dok2,
    tone: 'bg-indigo-400'
  }, {
    level: 3,
    label: 'Analyseren',
    count: dokDistribution.counts.dok3,
    correct: dokDistribution.correctCounts.dok3,
    accuracy: dokDistribution.accuracy.dok3,
    tone: 'bg-violet-500'
  }, {
    level: 4,
    label: 'Zelf bedenken',
    count: dokDistribution.counts.dok4,
    correct: dokDistribution.correctCounts.dok4,
    accuracy: dokDistribution.accuracy.dok4,
    tone: 'bg-fuchsia-500'
  }] as const).filter(d => d.count > 0) : [];
  const hasDokData = dokLevels.length > 0;
  const dokTotal = dokLevels.reduce((sum, d) => sum + d.count, 0);
  const dokUnknown = dokDistribution?.counts.unknown ?? 0;
  return <div className="min-h-screen bg-slate-50 font-sans flex flex-col" data-id="element-885" data-mp="2rsnbnu5siysb">
      {/* Minimal Header */}
      <header className="sticky top-0 z-20 bg-white/90 backdrop-blur-md border-b border-slate-200/80 px-4 py-2.5 flex items-center justify-between" data-id="element-886" data-mp="vpqv60v8tdqu">
        <button onClick={onExit} className="flex items-center gap-1.5 text-slate-500 hover:text-slate-900 font-medium transition-colors text-sm" aria-label="Terug naar dashboard" data-id="element-887" data-mp="10r6glu1c1m9r">
          
          <ChevronLeft className="w-5 h-5" data-id="element-888" data-mp="qxhbv3omvbxj" />
          <span className="hidden sm:inline" data-id="element-889" data-mp="cyzqvdq44djb">Terug</span>
        </button>
        <h1 className="text-sm font-bold text-slate-700 tracking-tight" data-id="element-890" data-mp="d1vmuwiegs54">
          Leerlingprofiel
        </h1>
        <div className="w-14" data-id="element-891" data-mp="31czhrrmmtig" />
      </header>

      {/* Scrollable Content */}
      <main className="flex-1 w-full max-w-3xl mx-auto px-4 sm:px-6 py-5 sm:py-8 space-y-6" data-id="element-892" data-mp="33ftsnztb8sz">
        {/* 1. Overall Result Hero */}
        <motion.section {...fadeUp} className="relative overflow-hidden rounded-2xl bg-slate-800 text-white shadow-lg" data-id="element-893" data-mp="1xeuolj7ojyfl">
          
          <div className="absolute -top-8 -right-8 opacity-[0.06]" data-id="element-894" data-mp="22d5zlx9poeze">
            <Award className="w-56 h-56" data-id="element-895" data-mp="3mvyln5cdjhhz" />
          </div>

          <div className="relative px-5 pt-5 pb-4 sm:px-7 sm:pt-7 sm:pb-5 flex flex-col sm:flex-row items-center gap-5" data-id="element-896" data-mp="3muf65gyjlah9">
            {/* Left: Level + Context */}
            <div className="flex-1 text-center sm:text-left" data-id="element-897" data-mp="hoyt0o3ehjb">
              <p className="text-slate-400 text-sm font-medium mb-1" data-id="element-898" data-mp="3rvunoz1igyuw">
                Groep {report.selectedGroup} · {formatDate(report.completedAt)}
              </p>
              <div className="flex items-baseline justify-center sm:justify-start gap-2.5 mb-1.5" data-id="element-899" data-mp="9jof1qra9hl7">
                <h2 className="text-4xl sm:text-5xl font-black tracking-tight leading-none" data-id="element-900" data-mp="5i2urkcf610z">
                  {overallEstimatedLevelCode}
                </h2>
                <span className="text-slate-400 font-medium text-base" data-id="element-901" data-mp="ehpzog4cxoys">
                  / {overallExpectedLevelCode}
                </span>
                {overallCitoLevel && <span className={`ml-2 px-2.5 py-0.5 rounded-full text-xs font-bold border ${overallCitoLevel.bgColor} ${overallCitoLevel.color} ${overallCitoLevel.borderColor}`} data-id="element-902" data-mp="2g8b5hkdirgzt">
                  
                    {overallCitoLevel.label}
                  </span>}
              </div>
              <p className="text-slate-300 text-sm sm:text-base font-medium max-w-sm leading-snug" data-id="element-903" data-mp="t5l2hcyo6vxi">
                {overallEstimatedTier === overallExpectedTier ? 'Je zit precies op het verwachte niveau. Keurig gewerkt!' : overallEstimatedTier > overallExpectedTier ? 'Uitstekend! Je scoort boven het verwachte niveau.' : 'Je bent goed op weg, maar er is nog ruimte voor groei.'}
              </p>
            </div>

            {/* Right: Score Ring */}
            <div className="w-24 h-24 sm:w-28 sm:h-28 relative flex-shrink-0" data-id="element-904" data-mp="1cuvh0nmojau1">
              <svg className="w-full h-full transform -rotate-90" data-id="element-905" data-mp="1gvffbuizokhr">
                <circle cx="50%" cy="50%" r="42%" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="7" data-id="element-906" data-mp="1lua9fsmepvh6" />
                
                <motion.circle cx="50%" cy="50%" r="42%" fill="none" stroke="white" strokeWidth="7" strokeLinecap="round" initial={{
                pathLength: 0
              }} animate={{
                pathLength: score.percentage / 100
              }} transition={{
                duration: 1.2,
                ease: 'easeOut'
              }} data-id="element-907" data-mp="1qsk5e5fyh03u" />
                
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-908" data-mp="1my0w7q20g0ay">
                <span className="text-2xl sm:text-3xl font-black leading-none" data-id="element-909" data-mp="2job5ix4nnvqk">
                  {score.percentage}%
                </span>
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5" data-id="element-910" data-mp="2ep8cyc5v3hzh">
                  Score
                </span>
              </div>
            </div>
          </div>

          {/* Stats Strip */}
          <div className="bg-slate-900/40 px-5 py-3 grid grid-cols-3 text-center text-sm divide-x divide-slate-600/30" data-id="element-911" data-mp="25uk4kd4zpk6g">
            <div data-id="element-912" data-mp="2xnsw9i1ihj2k">
              <span className="text-slate-400 text-[10px] uppercase font-bold tracking-wider block mb-0.5" data-id="element-913" data-mp="2xnaxadngsqqy">
                Goed
              </span>
              <span className="font-bold" data-id="element-914" data-mp="3h9qkjdvxjitb">
                {score.correctCount}/{score.totalQuestions}
              </span>
            </div>
            <div data-id="element-915" data-mp="37e6rcz8r2y89">
              <span className="text-slate-400 text-[10px] uppercase font-bold tracking-wider block mb-0.5" data-id="element-916" data-mp="37eq8weuhnklr">
                Tijd
              </span>
              <span className="font-bold" data-id="element-917" data-mp="3wovlq695ho7">{formatTime(time.totalSeconds)}</span>
            </div>
            <div data-id="element-918" data-mp="8vrpqfkp23he">
              <span className="text-slate-400 text-[10px] uppercase font-bold tracking-wider block mb-0.5" data-id="element-919" data-mp="8wb8tzm2yxx0">
                Per vraag
              </span>
              <span className="font-bold" data-id="element-920" data-mp="3q3n6osp3gv8q">
                {Math.round(time.avgPerQuestion)}s
              </span>
            </div>
          </div>
        </motion.section>

        {/* 2. Level per Subject */}
        <motion.section {...fadeUp} transition={stagger(1)} className="space-y-3" data-id="element-921" data-mp="2equgvnfv6gvb">
          
          <h3 className="flex items-center gap-2 text-lg font-bold text-slate-900 px-0.5" data-id="element-922" data-mp="2jlq6yi1jiu5s">
            <BarChart2 className="w-4.5 h-4.5 text-slate-500" data-id="element-923" data-mp="11od5g5bpmleq" />
            Niveau per Vakgebied
          </h3>
          <Card className="overflow-hidden shadow-sm" data-id="element-924" data-mp="wt7m9e9ozkuc">
            <div className="divide-y divide-slate-100" data-id="element-925" data-mp="1aputqaz9ttei">
              {estimatedLevels.map(level => {
              const growth = growthIndicators.find(g => g.moduleId === level.moduleId);
              const isUnknown = level.questionCount < MIN_QUESTIONS_FOR_ESTIMATE;
              return <div key={level.moduleId} className="px-4 py-3.5 sm:px-5" data-id="element-926" data-mp="e158caofqins">
                    <div className="flex items-center gap-3 mb-2" data-id="element-927" data-mp="rs0c58k2u9ki">
                      <span className="w-28 sm:w-36 font-bold text-slate-900 text-sm truncate" data-id="element-928" data-mp="rtbfx8divhbm">
                        {level.moduleTitle}
                      </span>
                      <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden relative" data-id="element-929" data-mp="3vg20j8flt79b">
                        {level.confidenceBand && !isUnknown && <div className="absolute top-0 bottom-0 bg-slate-200/50 z-0" style={{
                      left: `${level.confidenceBand.low / MAX_TIER * 100}%`,
                      width: `${(level.confidenceBand.high - level.confidenceBand.low) / MAX_TIER * 100}%`
                    }} data-id="element-930" data-mp="3rdfejo5m6gk1" />}
                        <div className="absolute top-0 bottom-0 w-px bg-slate-400 z-10" style={{
                      left: `${level.expectedTier / MAX_TIER * 100}%`
                    }} data-id="element-931" data-mp="17x447b9xbby0" />
                        
                        <motion.div initial={{
                      width: 0
                    }} animate={{
                      width: `${level.estimatedTier / MAX_TIER * 100}%`
                    }} transition={{
                      duration: 0.8,
                      ease: 'easeOut'
                    }} className={`h-full rounded-full ${isUnknown ? 'bg-slate-300' : level.delta > 0 ? 'bg-emerald-500' : level.delta === 0 ? 'bg-blue-500' : level.delta >= -1 ? 'bg-yellow-400' : 'bg-amber-500'}`} data-id="element-932" data-mp="t6n4pxgumw3r" />
                        
                      </div>
                      <div className="w-20 flex flex-col items-end flex-shrink-0" data-id="element-933" data-mp="2lumimcosyj9p">
                        <span className="font-black text-slate-800 text-sm" data-id="element-934" data-mp="2qse6mvmbtl8m">
                          {isUnknown ? '?' : level.estimatedLevelCode}
                        </span>
                        {!isUnknown && level.citoLevel && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded mt-0.5 ${level.citoLevel.bgColor} ${level.citoLevel.color}`} data-id="element-935" data-mp="2ztuhf4h1zpdd">
                          
                            {level.citoLevel.label}
                          </span>}
                      </div>
                    </div>
                    <div className="flex justify-between items-center text-xs pl-0 sm:pl-[7.5rem] sm:ml-3" data-id="element-936" data-mp="2c2ym3xde2c1d">
                      <span className="flex items-center gap-1.5 text-slate-500" data-id="element-937" data-mp="2gxm73m96oob7">
                        {growth && <span className={`w-1.5 h-1.5 rounded-full inline-block ${['groei_snel', 'gaat_goed', 'ik_groei'].includes(growth.level) ? 'bg-emerald-500' : ['let_op', 'extra_aandacht'].includes(growth.level) ? 'bg-amber-500' : ['krimpt', 'actie_vereist'].includes(growth.level) ? 'bg-red-500' : 'bg-slate-300'}`} data-id="element-938" data-mp="1kc2g9gpqx7nl" />}
                        {growth?.label || 'Te weinig data'}
                      </span>
                      <span className="text-slate-400 flex items-center gap-2" data-id="element-939" data-mp="2wh4j61iioozx">
                        {/* "SEM" is vakjargon en dit rapport wordt aan het
                             kind zelf getoond. De waarde zegt hoe zeker de
                             meting is; dat is precies wat er nu staat. */}
                        {level.sem !== undefined && !isUnknown && <span className="text-slate-400/80" title="Hoe zeker we van dit niveau zijn — hoe kleiner, hoe zekerder" data-id="element-940" data-mp="2htrqzty7fl0k">
                          
                            ±{level.sem.toFixed(1)}
                          </span>}
                        <span data-id="element-941" data-mp="3p5s57zb3biej">
                          {level.accuracy}% · {level.questionCount} vr.
                        </span>
                      </span>
                    </div>
                  </div>;
            })}
            </div>
            <div className="bg-slate-50 px-4 py-2.5 border-t border-slate-100 text-xs text-slate-400 flex items-start gap-1.5" data-id="element-942" data-mp="5k1abc9m6ntv">
              <Info className="w-3.5 h-3.5 mt-px flex-shrink-0" data-id="element-943" data-mp="3msy2btv1622x" />
              <span data-id="element-944" data-mp="3rr9dc7x2aimq">
                Streepje = het niveau dat bij jouw groep hoort. Balk = wat deze
                toets liet zien. De lichte zone laat zien hoe zeker we daarvan
                zijn: één toets is een momentopname.
              </span>
            </div>
          </Card>
        </motion.section>

        {/* 3. Tier Breakdown & Time (side by side) */}
        {(hasTierData || time.totalSeconds > 0) && <motion.section {...fadeUp} transition={stagger(2)} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-945" data-mp="eidb27473201">
          
            {hasTierData && <Card className="p-4 shadow-sm" data-id="element-946" data-mp="127fpz77g2gqu">
                <h4 className="flex items-center gap-2 font-bold text-slate-900 text-sm mb-3" data-id="element-947" data-mp="1710qb5wifmt7">
                  <Activity className="w-4 h-4 text-slate-500" data-id="element-948" data-mp="3fi359absymrg" />
                  Moeilijkheidsgraad
                </h4>
                <div className="space-y-2.5" data-id="element-949" data-mp="3agdvdy0bgf4y">
                  {byTier.map(tier => <div key={tier.tierRange} data-id="element-950" data-mp="35i2krl2760lo">
                      <div className="flex justify-between text-xs mb-1" data-id="element-951" data-mp="35kyhx7680l92">
                        <span className="font-medium text-slate-600" data-id="element-952" data-mp="30l2lpu3rly42">
                          Tier {tier.tierRange}
                        </span>
                        <span className="text-slate-400" data-id="element-953" data-mp="3t8ghhovq8nml">
                          {tier.accuracy}% ({tier.correctCount}/
                          {tier.totalQuestions})
                        </span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-954" data-mp="3jhlqy8bg71vc">
                        <div className="h-full bg-indigo-400 rounded-full transition-all" style={{
                  width: `${tier.accuracy}%`
                }} data-id="element-955" data-mp="kswomqyyx782" />
                  
                      </div>
                    </div>)}
                </div>
              </Card>}

            <Card className="p-4 shadow-sm" data-id="element-956" data-mp="sc5vacg3artf">
              <h4 className="flex items-center gap-2 font-bold text-slate-900 text-sm mb-3" data-id="element-957" data-mp="scxkn5j3ftb9">
                <Clock className="w-4 h-4 text-slate-500" data-id="element-958" data-mp="xah88w1olifv" />
                Tijdsbesteding
              </h4>
              <div className="space-y-4" data-id="element-959" data-mp="12bwu2qbonszb">
                <div className="flex items-center justify-between" data-id="element-960" data-mp="179erpkk92ls0">
                  <span className="text-slate-600 text-sm" data-id="element-961" data-mp="1796h5zblnsjz">Totale tijd</span>
                  <span className="font-bold text-slate-900" data-id="element-962" data-mp="1b9p9rvlbh2n8">
                    {formatTime(time.totalSeconds)}
                  </span>
                </div>
                <div className="flex items-center justify-between" data-id="element-963" data-mp="1l0jyr7py6gcd">
                  <span className="text-slate-600 text-sm" data-id="element-964" data-mp="1l2xpj4dlh6e4">Per vraag</span>
                  <span className="font-bold text-slate-900" data-id="element-965" data-mp="1q17pdsa9kv62">
                    {Math.round(time.avgPerQuestion)}s
                  </span>
                </div>
                <div className="flex items-center justify-between" data-id="element-966" data-mp="15ac3ynqgmemg">
                  <span className="text-slate-600 text-sm" data-id="element-967" data-mp="158iw55441ocv">Tempo</span>
                  <span className={`font-semibold px-2.5 py-0.5 rounded-full text-xs ${time.avgPerQuestion < 20 ? 'bg-emerald-100 text-emerald-700' : time.avgPerQuestion > 50 ? 'bg-amber-100 text-amber-700' : 'bg-blue-50 text-blue-700'}`} data-id="element-968" data-mp="10dc0mkkzvpz1">
                  
                    {time.avgPerQuestion < 20 ? 'Vlot' : time.avgPerQuestion > 50 ? 'Rustig' : 'Gemiddeld'}
                  </span>
                </div>
              </div>
            </Card>
          </motion.section>}

        {/* 3b. DOK / Denkdiepte breakdown (R3-DOK1 surfacing) */}
        {hasDokData && <motion.section {...fadeUp} transition={stagger(2)} data-id="element-969" data-mp="1szwqthpqhw10">
            <Card className="p-4 shadow-sm" data-id="element-970" data-mp="1sydbpuftdr0t">
              <div className="flex items-start justify-between gap-2 mb-3" data-id="element-971" data-mp="3kqadxo8ovki">
                <h4 className="flex items-center gap-2 font-bold text-slate-900 text-sm" data-id="element-972" data-mp="3us2scewy5d1h">
                  <Brain className="w-4 h-4 text-slate-500" data-id="element-973" data-mp="2a9s62dg5n6vk" />
                  Denkdiepte
                </h4>
                {/* De vakterm (Webb's Depth of Knowledge) hoort in de
                   leerkrachtrapportage, niet op het scherm van een kind van
                   acht. "Denkdiepte" zegt hier hetzelfde en is te begrijpen.
                   "Geclassificeerd" en "zonder DOK" idem. */}
                <span className="text-[10px] text-slate-400 font-medium" title="Van onthouden naar toepassen naar zelf uitzoeken — hoe moeilijk het denkwerk was." data-id="element-974" data-mp="1rjajkqlx17in">
                
                  {dokTotal} vragen
                  {dokUnknown > 0 ? ` · ${dokUnknown} niet ingedeeld` : ''}
                </span>
              </div>
              <div className="space-y-2.5" data-id="element-975" data-mp="1ml90i5ohp876">
                {dokLevels.map(d => {
              const sharePct = dokTotal > 0 ? d.count / dokTotal * 100 : 0;
              return <div key={d.level} data-id="element-976" data-mp="3gy7b6qe3q6v1">
                      <div className="flex justify-between text-xs mb-1" data-id="element-977" data-mp="3gww4oit9ul08">
                        <span className="font-medium text-slate-600" data-id="element-978" data-mp="23rvtyw4i7ivd">
                          {d.label}
                        </span>
                        <span className="text-slate-400" data-id="element-979" data-mp="1zqakzlv3boqg">
                          {d.accuracy !== null ? `${d.accuracy}%` : '—'} (
                          {d.correct}/{d.count})
                        </span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-980" data-mp="1l0uzakaf29hm">
                        <motion.div initial={{
                    width: 0
                  }} animate={{
                    width: `${sharePct}%`
                  }} transition={{
                    duration: 0.6,
                    ease: 'easeOut'
                  }} className={`h-full rounded-full ${d.tone}`} data-id="element-981" data-mp="1kyi8dhmokosf" />
                      
                      </div>
                    </div>;
            })}
              </div>
              <p className="mt-3 text-[10px] text-slate-400 leading-relaxed" data-id="element-982" data-mp="31el63ysx7buy">
                Hoger DOK = meer denkwerk. Een goede toets dekt meerdere
                niveaus.
              </p>
            </Card>
          </motion.section>}

        {/* 4. Strengths & Focus Areas */}
        {hasAnalysis && <motion.section {...fadeUp} transition={stagger(3)} className="space-y-3" data-id="element-983" data-mp="3inx5djd948e4">
          
            <h3 className="flex items-center gap-2 text-lg font-bold text-slate-900 px-0.5" data-id="element-984" data-mp="3no1n1cpwb95x">
              <Target className="w-4.5 h-4.5 text-slate-500" data-id="element-985" data-mp="2q01a8j3byojg" />
              Inhoudelijke Analyse
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-986" data-mp="2uy2sx2wuglue">
              {/* Strengths */}
              <Card className="p-4 shadow-sm border-emerald-100/60 bg-emerald-50/20" data-id="element-987" data-mp="2zxpga56dzigx">
                <h4 className="flex items-center gap-2 font-bold text-emerald-800 text-sm mb-3" data-id="element-988" data-mp="3402b3yjlhvyx">
                  <Star className="w-4 h-4 text-emerald-500 fill-emerald-500" data-id="element-989" data-mp="f89jmtkhkzjd" />
                  Sterke Punten
                </h4>
                {strongAreas.length > 0 && <p className="text-xs text-emerald-600 mb-3 font-medium" data-id="element-990" data-mp="jbekkrox4t5w">
                    {strongAreas.join(', ')}
                  </p>}
                {strengthsBySkill.length > 0 ? <ul className="space-y-2" data-id="element-991" data-mp="o7b3j8vp46cx">
                    {strengthsBySkill.slice(0, 3).flatMap(area => area.skills.slice(0, 2).map(skill => <li key={`${area.moduleId}-${skill.skillName}`} className="flex items-start gap-2" data-id="element-992" data-mp="t1fbnvgp6kfl">
                  
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" data-id="element-993" data-mp="18g0zon8tid1" />
                          <div className="min-w-0" data-id="element-994" data-mp="59i4iho70gkt">
                            <span className="font-semibold text-emerald-900 text-xs capitalize" data-id="element-995" data-mp="5a1l9v23wyx9">
                              {skill.skillName}
                            </span>
                            <span className="text-emerald-500 text-xs ml-1.5" data-id="element-996" data-mp="f8qpwm4zsmvk">
                              {Math.round(skill.avgAccuracy)}%
                            </span>
                          </div>
                        </li>))}
                  </ul> : <p className="text-emerald-500/70 text-xs italic" data-id="element-997" data-mp="1qoxkt7e9tok4">
                    Nog geen specifieke sterke punten.
                  </p>}
              </Card>

              {/* Focus Areas */}
              <Card className="p-4 shadow-sm border-amber-100/60 bg-amber-50/20" data-id="element-998" data-mp="368epb48qthk2">
                <h4 className="flex items-center gap-2 font-bold text-amber-800 text-sm mb-3" data-id="element-999" data-mp="31d9304bfi0vg">
                  <AlertCircle className="w-4 h-4 text-amber-500" data-id="element-1000" data-mp="2wfz8ia96mz0r" />
                  Aandachtspunten
                </h4>
                {weakAreas.length > 0 && <p className="text-xs text-amber-600 mb-3 font-medium" data-id="element-1001" data-mp="2nf1aq9pc7nqm">
                    {weakAreas.join(', ')}
                  </p>}
                {focusAreasBySkill.length > 0 ? <ul className="space-y-2" data-id="element-1002" data-mp="2iiaunmdj8s57">
                    {focusAreasBySkill.slice(0, 3).flatMap(area => area.skills.slice(0, 2).map(skill => <li key={`${area.moduleId}-${skill.skillName}`} className="flex items-start gap-2" data-id="element-1003" data-mp="gpmmqzpy1gj3">
                  
                          <TrendingUp className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" data-id="element-1004" data-mp="brbeuuj7lg3i" />
                          <div className="min-w-0" data-id="element-1005" data-mp="265gog4f665ij">
                            <span className="font-semibold text-amber-900 text-xs capitalize" data-id="element-1006" data-mp="2awovwuwxvgt2">
                              {skill.skillName}
                            </span>
                            <span className="text-amber-500 text-xs ml-1.5" data-id="element-1007" data-mp="1w67uhbkb0qw6">
                              {Math.round(skill.avgAccuracy)}%
                            </span>
                          </div>
                        </li>))}
                  </ul> : <p className="text-amber-500/70 text-xs italic" data-id="element-1008" data-mp="2tw2u5p1pbdpd">
                    Geen specifieke aandachtspunten.
                  </p>}
              </Card>
            </div>
          </motion.section>}

        {/* 5. Action Plan & Recommendations */}
        {(hasActionPlan || recommendation) && <motion.section {...fadeUp} transition={stagger(4)} className="space-y-3" data-id="element-1009" data-mp="14gd73kg0bba5">
          
            <h3 className="flex items-center gap-2 text-lg font-bold text-slate-900 px-0.5" data-id="element-1010" data-mp="18izpkuqsfjuo">
              <Lightbulb className="w-4.5 h-4.5 text-slate-500" data-id="element-1011" data-mp="10yp54x19mynw" />
              Advies & Actieplan
            </h3>

            <Card className="p-5 shadow-sm space-y-5" data-id="element-1012" data-mp="w0nmgd7r51cy">
              {recommendation && <p className="text-slate-700 text-sm font-medium leading-relaxed" data-id="element-1013" data-mp="1ax4zd8vlao1m">
                  {recommendation}
                </p>}

              {/* Begin Met */}
              {actionPlan.beginMet && <div className="bg-orange-50 border border-orange-100 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-id="element-1014" data-mp="i8h7gugc50f3">
                  <div className="min-w-0" data-id="element-1015" data-mp="dapcfrke8y6o">
                    <div className="flex items-center gap-1.5 mb-0.5" data-id="element-1016" data-mp="ryafxml6kzf4">
                      <Zap className="w-3.5 h-3.5 text-orange-500" data-id="element-1017" data-mp="rzll9qu9yf8c" />
                      <span className="text-[10px] font-bold text-orange-700 uppercase tracking-wider" data-id="element-1018" data-mp="n6iyc1k06414">
                        Prioriteit
                      </span>
                    </div>
                    <div className="font-bold text-slate-900 text-sm" data-id="element-1019" data-mp="3qn7x31dcg9g8">
                      {actionPlan.beginMet.familyDescription}
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-1020" data-mp="2xyh9t86sfdir">
                      {actionPlan.beginMet.moduleTitle} ›{' '}
                      {actionPlan.beginMet.skillName}
                    </div>
                  </div>
                  {onPracticeWeakAreas && <Button onClick={onPracticeWeakAreas} variant="primary" size="sm" className="bg-orange-500 hover:bg-orange-600 whitespace-nowrap flex-shrink-0" data-id="element-1021" data-mp="37p2ijog6dyfe">
                
                      Oefenen
                    </Button>}
                </div>}

              {/* Ga Verder Met */}
              {actionPlan.gaVerderMet.length > 0 && <div data-id="element-1022" data-mp="3llz9dpwanfwa">
                  <h5 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-1023" data-mp="3qmwnnre6npsi">
                    Daarna verder met
                  </h5>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-1024" data-mp="1rbvjixtly42d">
                    {actionPlan.gaVerderMet.map((item, idx) => <div key={idx} className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 flex justify-between items-center" data-id="element-1025" data-mp="1waypqllbqgbx" data-mpi={idx}>
                  
                        <div className="min-w-0" data-id="element-1026" data-mp="1jy4f8rfxh7jg">
                          <div className="font-semibold text-slate-800 text-xs truncate" data-id="element-1027" data-mp="1ezt66ixgj12a">
                            {item.familyDescription}
                          </div>
                          <div className="text-[10px] text-slate-400" data-id="element-1028" data-mp="19ynb4egbr1pl">
                            {item.moduleTitle}
                          </div>
                        </div>
                        {onStartPracticeForModule && <button onClick={() => onStartPracticeForModule(item.moduleId)} className="p-1.5 text-slate-300 hover:text-orange-500 transition-colors flex-shrink-0" aria-label={`Oefen ${item.familyDescription}`} data-id="element-1029" data-mp="22nkt0ggmiwht">
                    
                            <Play className="w-3.5 h-3.5" data-id="element-1030" data-mp="mc71t8gensed" />
                          </button>}
                      </div>)}
                  </div>
                </div>}

              {/* Uitdaging */}
              {actionPlan.uitdaging && <div className="bg-sky-50 border border-sky-100 rounded-xl p-4" data-id="element-1031" data-mp="jr92nr23w682">
                  <div className="flex items-center gap-1.5 mb-0.5" data-id="element-1032" data-mp="oorepqbf71ka">
                    <Sparkles className="w-3.5 h-3.5 text-sky-500" data-id="element-1033" data-mp="9ss5udghkxe3" />
                    <span className="text-[10px] font-bold text-sky-700 uppercase tracking-wider" data-id="element-1034" data-mp="9umhf15vbd5i">
                      Extra Uitdaging
                    </span>
                  </div>
                  <div className="font-bold text-slate-900 text-sm" data-id="element-1035" data-mp="yx3fd6r1l25">
                    {actionPlan.uitdaging.suggestion}
                  </div>
                  <div className="text-xs text-slate-500" data-id="element-1036" data-mp="3n7y9zf9ijjys">
                    {actionPlan.uitdaging.moduleTitle}
                  </div>
                </div>}
            </Card>
          </motion.section>}

        {/* 6. Bottom Actions */}
        <motion.section {...fadeUp} transition={stagger(5)} className="pt-4 pb-6 flex flex-col sm:flex-row items-center justify-center gap-3" data-id="element-1037" data-mp="3e81iz6zni90k">
          
          {onPracticeWeakAreas && <Button onClick={onPracticeWeakAreas} variant="primary" size="lg" className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 shadow-sm" data-id="element-1038" data-mp="2zj9m201x30ff">
            
              <BookOpen className="w-4 h-4 mr-2" data-id="element-1039" data-mp="38kpuw3d32uhj" />
              Start met Oefenen
            </Button>}
          {onRetake && <Button onClick={onRetake} variant="outline" size="lg" className="w-full sm:w-auto" data-id="element-1040" data-mp="33l4vjru04k4a">
            
              Opnieuw maken
            </Button>}
          <Button onClick={onExit} variant="ghost" size="lg" className="w-full sm:w-auto text-slate-500" data-id="element-1041" data-mp="3igsub1gjwf0s">
            
            Terug naar Dashboard
          </Button>
        </motion.section>
      </main>
    </div>;
}